import React from "react";
import { Icon } from "@/components/ui/display/Icon";
import type { ChipProps } from "./types";
import styles from "./Chip.module.css";

/**
 * Chip component - A pill-shaped element for displaying tags, selections, or filters.
 * Supports deletable and clickable modes.
 *
 * - When only clickable (no delete button): renders as semantic <button>
 * - When clickable AND deletable: renders as <div role="button"> to allow nested delete button
 * - When not clickable: renders as <span>
 *
 * Ported from Vue Chip.vue component.
 */
export const Chip = React.forwardRef<
  HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
  ChipProps
>(
  (
    {
      label,
      deletable = false,
      compact = false,
      clickable = false,
      selected = false,
      onDelete,
      onClick,
      "data-testid": testId,
    },
    ref,
  ) => {
    const dataAttributes = {
      className: styles.chip,
      "data-size": compact ? "compact" : "regular",
      "data-selected": selected ? "true" : "false",
      "data-clickable": clickable ? "true" : "false",
      "data-deletable": deletable ? "true" : "false",
      "data-testid": testId,
    };

    const handleClick = () => {
      if (clickable && onClick) {
        onClick();
      }
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDelete) {
        onDelete();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (clickable && onClick) {
          onClick();
        }
      }
    };

    const handleDeleteKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) {
          onDelete();
        }
      }
    };

    const labelContent = <span data-component-name="label">{label}</span>;

    const deleteButton = deletable && (
      <button
        type="button"
        data-component-name="delete"
        onClick={handleDelete}
        onKeyDown={handleDeleteKeyDown}
        aria-label={`Remove ${label}`}
        data-testid={testId ? `${testId}-delete` : undefined}
      >
        <Icon name="x" data-component-name="delete-icon" aria-hidden />
      </button>
    );

    // When clickable but NOT deletable: use semantic <button>
    if (clickable && !deletable) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          {...dataAttributes}
          onClick={onClick}
        >
          {labelContent}
        </button>
      );
    }

    // When clickable AND deletable: use div with role="button" (can't nest buttons)
    if (clickable && deletable) {
      return (
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          {...dataAttributes}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
        >
          {labelContent}
          {deleteButton}
        </div>
      );
    }

    // Non-clickable chips render as span
    return (
      <span ref={ref as React.Ref<HTMLSpanElement>} {...dataAttributes}>
        {labelContent}
        {deleteButton}
      </span>
    );
  },
);

Chip.displayName = "Chip";

export default Chip;
